import cn from 'classnames';
import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { useChange, useForwardedRef, useOutsideClick, usePropState, } from '@anton.bobrov/react-hooks';
import { Portal } from 'react-portal';
import { prefixedClassName } from '../../utils/prefixedClassName';
import { usePosition } from './internal/usePosition';
import { getViewportGap } from './internal/getViewportGap';
import { Anchor as DefaultAnchorComponent } from './Anchor';
var DefaultAnchor = memo(DefaultAnchorComponent);
export var BasePopup = forwardRef(function (_a, forwardedRef) {
    var className = _a.className, style = _a.style, children = _a.children, anchor = _a.anchor, _b = _a.renderAnchor, Anchor = _b === void 0 ? DefaultAnchor : _b, positionRef = _a.positionRef, isActiveProp = _a.isActive, onActive = _a.onActive, _c = _a.showEvent, showEvent = _c === void 0 ? 'click' : _c, verticalAlignmentProp = _a.verticalAlignment, horizontalAlignmentProp = _a.horizontalAlignment, gap = _a.gap, _d = _a.viewportGap, viewportGap = _d === void 0 ? getViewportGap : _d, viewportRef = _a.viewportRef, isDisabled = _a.isDisabled, _e = _a.hasOutsideClick, hasOutsideClick = _e === void 0 ? true : _e, lifetime = _a.lifetime;
    var contentRef = useForwardedRef(forwardedRef);
    var anchorRef = useRef(null);
    var _f = useState(false), canRender = _f[0], setCanRender = _f[1];
    var _g = usePropState(!!isActiveProp), isActive = _g[0], setIsActive = _g[1];
    // `onActive` callback
    useChange(function (value) {
        onActive === null || onActive === void 0 ? void 0 : onActive(value);
    }, isActive);
    // render
    useEffect(function () {
        if (isActive) {
            setCanRender(true);
        }
    }, [isActive]);
    // lifetime
    useEffect(function () {
        if (!isActive || !lifetime) {
            return undefined;
        }
        var timeout = setTimeout(function () { return setIsActive(false); }, lifetime);
        return function () { return clearTimeout(timeout); };
    }, [isActive, setIsActive, lifetime]);
    useOutsideClick(anchorRef, function (event) {
        var _a;
        if (!canRender || !hasOutsideClick) {
            return;
        }
        if (!((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            setIsActive(false);
        }
    });
    var _h = usePosition({
        positionRef: positionRef !== null && positionRef !== void 0 ? positionRef : anchorRef,
        verticalAlignment: verticalAlignmentProp,
        horizontalAlignment: horizontalAlignmentProp,
        gap: gap,
        viewportGap: viewportGap,
        viewportRef: viewportRef,
        isDisabled: !canRender || !!isDisabled,
    }), verticalAlignment = _h.verticalAlignment, horizontalAlignment = _h.horizontalAlignment, positionStyle = _h.style;
    return (React.createElement(React.Fragment, null,
        React.createElement(Anchor, { ref: anchorRef, activate: function () { return setIsActive(true); }, deactivate: function () { return setIsActive(false); }, isActive: isActive, anchor: anchor, showEvent: showEvent }),
        canRender && !isDisabled && (React.createElement(Portal, null,
            React.createElement("div", { ref: contentRef, className: prefixedClassName('base-popup'), style: positionStyle },
                React.createElement("div", { className: cn(prefixedClassName('base-popup__container'), isActive && prefixedClassName('show'), prefixedClassName("vertical_".concat(verticalAlignment)), prefixedClassName("horizontal_".concat(horizontalAlignment)), className), style: style, onAnimationEnd: function () {
                        if (!isActive) {
                            setCanRender(false);
                        }
                    } }, children))))));
});
BasePopup.displayName = 'BasePopup';
export * from './Anchor/types';
//# sourceMappingURL=index.js.map